import { styled } from '@mui/material'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Typography from '@mui/material/Typography'
import React from 'react'

import ExternalLink from '@/app/component/atom/external-link'
import IconText from '@/app/component/layout/icon-text'
import CustomDialog from '@/app/component/wrapper/custom-dialog'
import Payment from '@/app/module/payments/component/payment'
import { useGetBalance } from '@/app/module/payments/hooks/use-get-balance'
import { useGetCreditCards } from '@/app/module/payments/hooks/use-get-credit-cards'

import { Plan } from '../types'
import Benefits from './benefits'

const getSteps = (): string[] => ['Select Plan', 'Charge Balance', 'Confirm']

export type PlanConfirmation = {
  display: boolean
  text: React.ReactNode
  title: string
  label?: string
  price?: number
  cycle: boolean
  plan: Plan
  callback: () => void
}

type Props = {
  planConfirmation: PlanConfirmation

  onClose: () => void
  onConfirm: () => void
}

/**
 * Subscribe to a plan wizard dialog
 */
const SubscribePlan: React.FC<Props> = (props) => {
  const { planConfirmation, onClose, onConfirm } = props
  const { title, text, plan, cycle } = planConfirmation
  const [activeStep, setActiveStep] = React.useState<number>(0)
  const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false)
  const { data: orgBalance } = useGetBalance()
  const { data: cards = [] } = useGetCreditCards()
  const steps = getSteps()
  const price = Number(cycle ? plan.priceYearly : plan.priceMonthly)
  const oldPrice = Number(plan.priceMonthly) * 12
  const balance = orgBalance?.balance ?? 0
  const isBalanceEnough = balance >= price
  const minPayment = price - balance

  const hasCard = cards.length > 0
  const handleNext = () => setActiveStep(activeStep + 1)
  const isConfirmationStep = activeStep === steps.length - 1
  const handleDialogClose = React.useCallback(() => {
    if (!isConfirmationStep) {
      onClose()
    }
  }, [isConfirmationStep, onClose])

  if (isBalanceEnough && activeStep === 1) {
    setActiveStep(2)
  }

  return (
    <CustomDialog onClose={handleDialogClose} fullWidth maxWidth="lg" id="delete-dialog" open={true}>
      <DialogTitle>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <IconText>
            <Icon>card_membership</Icon>
            <Typography variant="h6">{title}</Typography>
          </IconText>
          {!isConfirmationStep && (
            <IconButton aria-label="Close" onClick={onClose}>
              <Icon>close</Icon>
            </IconButton>
          )}
        </div>
      </DialogTitle>
      <DialogContent>
        <Root>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <StepContainer>
            {activeStep === 0 && (
              <PlanInformationContainer>
                <PlanInformationRow>
                  <PlanTitle>
                    <Typography variant="h5">
                      Selected Plan:{' '}
                      <strong>
                        <PlanLabel>{plan.label}</PlanLabel>
                      </strong>
                    </Typography>
                  </PlanTitle>
                  <PlanDetails>
                    <Benefits plan={plan} />
                  </PlanDetails>
                </PlanInformationRow>
                <PlanPriceRow>
                  {cycle && <OldPrice>${oldPrice.toFixed(0)}</OldPrice>}
                  <TotalRow>
                    <TotalLabel variant="h5">Total Due:</TotalLabel>
                    <div>
                      <Typography component="div" variant="h5">
                        <PlanLabel>
                          <strong>${price}</strong>
                        </PlanLabel>
                      </Typography>
                    </div>
                  </TotalRow>
                  <ToolbarContainer>
                    <CycleContainer>
                      <Typography variant="caption" color="textSecondary">
                        (billed {cycle ? 'yearly' : 'monthly'})
                      </Typography>
                    </CycleContainer>
                    <ChargeTextContainer>
                      <FormControlLabel
                        control={<Checkbox checked={termsAccepted} onChange={() => setTermsAccepted(!termsAccepted)} />}
                        label={
                          <Typography color="textSecondary" variant="subtitle1" style={{ textAlign: 'left' }}>
                            I understand that Text messages, voice minutes, virtual phone numbers, airtime top-ups, and
                            other usage fees are charged{' '}
                            <span>
                              <strong>in addition</strong>
                            </span>{' '}
                            to Subscription Plan fees. For more information{' '}
                            <ExternalLink href="https://www.engagespark.com/pricing" target="_blank">
                              click here
                            </ExternalLink>
                          </Typography>
                        }
                      />
                    </ChargeTextContainer>
                  </ToolbarContainer>
                </PlanPriceRow>
              </PlanInformationContainer>
            )}
            {activeStep === 1 && (
              <>
                <Typography variant="h6" style={{ textAlign: 'left', marginBottom: '15px' }}>
                  {hasCard
                    ? 'Please add enough balance to subscribe to your new plan'
                    : 'Please add a credit card and charge it to subscribe to your new plan'}
                </Typography>
                <Payment amount={price} minimum={minPayment.toFixed(0)} />
              </>
            )}
            {activeStep === 2 && (
              <Typography component="div" variant="h4" color="textSecondary">
                {text}
              </Typography>
            )}
          </StepContainer>
        </Root>
      </DialogContent>
      {activeStep !== 1 && (
        <DialogActions>
          <Button variant="outlined" id="plan-modal-cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            id="step-forwaard"
            disabled={activeStep === 0 && !termsAccepted}
            onClick={isConfirmationStep ? onConfirm : handleNext}
          >
            {isConfirmationStep ? 'Confirm' : `Continue with ${cycle ? 'yearly' : 'monthly'} billing`}
          </Button>
        </DialogActions>
      )}
    </CustomDialog>
  )
}

type StepContainerProps = {
  children: React.ReactNode
  style?: React.CSSProperties
}

const StepContainer: React.FC<StepContainerProps> = ({ children, style }) => (
  <div
    style={{
      ...style,
      padding: '15px 30px',
      textAlign: 'center',
    }}
  >
    {children}
  </div>
)

const Root = styled('div')({
  width: '100%',
})

const PlanLabel = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
}))

const PlanInformationContainer = styled('div')({})

const PlanInformationRow = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: 'rgb(245, 245, 245)',
  boxShadow: '0px 0px 0px 1px rgba(224, 224, 224, 1)',
})

const PlanTitle = styled('div')({
  alignSelf: 'center',
  paddingRight: '100px',
})

const PlanDetails = styled('div')({})

const PlanPriceRow = styled('div')({
  textAlign: 'right',
  paddingTop: '15px',
})

const TotalRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
})

const TotalLabel = styled(Typography)({
  marginRight: '15px',
  alignSelf: 'center',
})

const OldPrice = styled('div')({
  textDecoration: 'line-through',
  marginRight: '3px',
  color: '#ef6c00',
  fontSize: '18px',
  display: 'inline-block',
})

const ToolbarContainer = styled('div')({})

const CycleContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
})

const ChargeTextContainer = styled('div')({
  display: 'flex',
})

export default SubscribePlan
